import * as Api from '@ViewModels';
import { IAutomationInfo } from '../../../../../models';
import { ILocalNotification } from '../../../../../models/LocalNotifications';
import { useCompanyTimelineData, useContactTimelineData } from './useTimelineData';

function useTimeline({
	id,
	timelineData,
	setShouldStartPollingForRelationshipUpdate,
}: {
	emailAddresses?: Api.EmailAddress[];
	eventLoggingCategory: string;
	id: string;
	timelineData: ReturnType<typeof useContactTimelineData> | ReturnType<typeof useCompanyTimelineData>;
	setShouldStartPollingForRelationshipUpdate?(poll: boolean): void;
}) {
	const onCreateOrUpdateNotificationReceived = (notification: ILocalNotification<Api.IActionItem>) => {
		const actionItemModel = notification.info;
		const entityIds = [
			...actionItemModel.referencedEntities.contacts,

			...actionItemModel.referencedEntities.companies,
		].map(x => x.entity.id);
		if (entityIds.indexOf(id) >= 0) {
			timelineData.invalidateTimeline();
		}
	};
	const onTimelineRefreshNotificationReceived = () => {
		timelineData.invalidateTimeline();
	};

	const onNoteCreateOrUpdateNotificationReceived = (notification: ILocalNotification<Api.INote>) => {
		if (!timelineData.isLoading) {
			const noteModel = notification.info;

			if (noteModel.referencedEntities) {
				const entityIds = [...noteModel.referencedEntities.contacts, ...noteModel.referencedEntities.companies].map(
					x => x.entity.id
				);
				if (setShouldStartPollingForRelationshipUpdate) {
					setShouldStartPollingForRelationshipUpdate(true);
				}
				if (entityIds.indexOf(id) >= 0) {
					timelineData.invalidateTimeline();
				}
			}
		}
	};

	const onAutomationEdited = (notification?: ILocalNotification<IAutomationInfo>) => {
		if (
			!!id &&
			notification?.info?.contact?.id === id &&
			notification?.info?.automation?.steps?.some(x => x.step?._type === 'ActionItemAutomationStep')
		) {
			timelineData.invalidateTimeline();
		}
	};

	return {
		onAutomationEdited,
		onCreateOrUpdateNotificationReceived,
		onNoteCreateOrUpdateNotificationReceived,
		onTimelineRefreshNotificationReceived,
		timelineData,
	};
}

export const useContactTimeline = ({
	emailAddresses,
	eventTypes,
	contactId,
	setShouldStartPollingForRelationshipUpdate,
}: {
	emailAddresses?: Api.EmailAddress[];
	eventTypes: Api.TimelineEventTypes[];
	contactId: string;
	setShouldStartPollingForRelationshipUpdate?(poll: boolean): void;
}) => {
	const timelineData = useContactTimelineData({ contactId, eventTypes });
	const onEmailsSent = (notification: ILocalNotification<Api.EmailMessageViewModel>) => {
		if (!!notification.info.options && !!notification.info.options.saveAsNote) {
			let scheduleReload = false;
			if (notification.info instanceof Api.EmailMessageViewModel) {
				const entityEmailAddresses = (emailAddresses || []).map(x => x.value).filter(x => !!x);
				const emailMessage = notification.info;
				if (emailMessage.contactsToAdd.length > 0) {
					const recipientIds = emailMessage.contactsToAdd
						.filter(x => !emailMessage.contactsToOmit.has(x))
						.map(x => x.id);
					scheduleReload = recipientIds.indexOf(contactId) >= 0;
				} else if (entityEmailAddresses.length > 0) {
					const addresses = (emailMessage.contactsToAdd.toArray() || [])
						.map(y => {
							const emailAddress = emailMessage.getPreferredEmailAddressForContact(y);
							return !!emailAddress && !!emailAddress.value ? emailAddress.value : null;
						})
						.filter(z => !!z);
					const matchingEmail = entityEmailAddresses.find(x => {
						return !!addresses.find(y => y === x);
					});
					scheduleReload = !!matchingEmail;
				}
			}
			if (scheduleReload) {
				timelineData.invalidateTimeline();
				if (setShouldStartPollingForRelationshipUpdate) {
					setShouldStartPollingForRelationshipUpdate(true);
				}
			}
		}
	};
	const timelineProps = useTimeline({
		eventLoggingCategory: 'ContactTimeline',
		id: contactId,
		timelineData,
		setShouldStartPollingForRelationshipUpdate,
	});
	return {
		...timelineProps,
		onEmailsSent,
	};
};

export const useCompanyTimeline = ({
	eventTypes,
	companyId,
}: {
	eventTypes: Api.TimelineEventTypes[];
	companyId: string;
}) => {
	const timelineData = useCompanyTimelineData({ companyId, eventTypes });
	return useTimeline({
		eventLoggingCategory: 'ComapnyTimeline',
		id: companyId,
		timelineData,
	});
};
